import { useState, useEffect } from 'react';
// The default '@stripe/stripe-js' entrypoint injects js.stripe.com (~244 KB) as
// a module-import SIDE EFFECT — on every page, for every visitor. The /pure
// entrypoint loads Stripe.js only when loadStripe() is actually called (i.e.
// when this payment form renders).
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useBilling } from '../../context/BillingContext';
import { billingService } from '../../services/api/billingService';
import './PaymentMethodForm.css';

interface Props {
  onSuccess: (paymentMethodId: string) => void;
  onCancel: () => void;
}

function CardForm({ onSuccess, onCancel }: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const { addPaymentMethod } = useBilling();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    try {
      // Get setup intent from server
      const setupIntent = await billingService.getSetupIntent();

      // Confirm card setup
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      const { error: stripeError, setupIntent: confirmedIntent } = await stripe.confirmCardSetup(
        setupIntent.clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message || 'Card setup failed');
      } else if (confirmedIntent?.payment_method) {
        const pmId = typeof confirmedIntent.payment_method === 'string'
          ? confirmedIntent.payment_method
          : confirmedIntent.payment_method.id;

        // Add payment method to our backend
        await addPaymentMethod(pmId, true);
        onSuccess(pmId);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save payment method');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="card-element-wrapper">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#1a1a2e',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                '::placeholder': {
                  color: '#94a3b8',
                },
              },
              invalid: {
                color: '#dc2626',
              },
            },
          }}
        />
      </div>

      {error && <div className="payment-error">{error}</div>}

      <div className="payment-form-actions">
        <button
          type="button"
          className="btn-cancel"
          onClick={onCancel}
          disabled={isProcessing}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-save"
          disabled={!stripe || isProcessing}
        >
          {isProcessing ? 'Processing...' : 'Save Card'}
        </button>
      </div>

      <p className="payment-security-note">
        <span className="lock-icon">&#128274;</span>
        Your payment info is securely processed by Stripe. We never store your full card details.
      </p>
    </form>
  );
}

export function PaymentMethodForm(props: Props) {
  const { stripePublishableKey } = useBilling();
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    if (stripePublishableKey) {
      setStripePromise(loadStripe(stripePublishableKey));
    }
  }, [stripePublishableKey]);

  if (!stripePublishableKey || !stripePromise) {
    return (
      <div className="payment-form-loading">
        <p>Loading payment form...</p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <CardForm {...props} />
    </Elements>
  );
}
